import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Box, IconButton, Typography, Paper } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { useState, useEffect } from 'react';

const columnStyles = {
  name: { width: '120px', minWidth: '120px', maxWidth: '120px' },
  modelNo: { width: '80px', minWidth: '80px', maxWidth: '80px' },
  sku: { width: '100px', minWidth: '100px', maxWidth: '100px' },
  quantity: { width: '80px', minWidth: '80px', maxWidth: '80px' },
  costPerUnit: { width: '100px', minWidth: '100px', maxWidth: '100px' },
  totalCost: { width: '100px', minWidth: '100px', maxWidth: '100px' },
  status: { width: '80px', minWidth: '80px', maxWidth: '80px' },
  actions: { width: '80px', minWidth: '80px', maxWidth: '80px', textAlign: 'center' },
};

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'retired', label: 'Retired' },
  { value: 'not_applicable', label: 'Not Applicable' }
];

const InventoryItemsTable = ({ items, onEdit, onDelete, scrollThreshold = 768 }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columns = [
    {
      dataField: 'name',
      text: 'Item Name',
      sort: true,
      headerStyle: () => columnStyles.name,
      style: () => columnStyles.name,
    },
    {
      dataField: 'modelNo',
      text: 'Model Number',
      headerStyle: () => columnStyles.modelNo,
      style: () => columnStyles.modelNo,
      formatter: (cell) => cell || 'N/A',
    },
    {
      dataField: 'sku',
      text: 'SKU',
      headerStyle: () => columnStyles.sku,
      style: () => columnStyles.sku,
      formatter: (cell) => (cell?.length ? cell.join(', ') : 'N/A'),
    },
    {
      dataField: 'quantity.active',
      text: 'Quantity',
      sort: true,
      headerStyle: () => columnStyles.quantity,
      style: () => columnStyles.quantity,
      formatter: (cell) => (cell !== undefined ? cell : 0),
    },
    {
      dataField: 'costPerUnit',
      text: 'Cost/Unit',
      sort: true,
      headerStyle: () => columnStyles.costPerUnit,
      style: () => columnStyles.costPerUnit,
      formatter: (cell) =>
        cell ? `$${Number(cell).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A',
    },
    {
      dataField: 'totalCost',
      text: 'Total Cost',
      sort: true,
      headerStyle: () => columnStyles.totalCost,
      style: () => columnStyles.totalCost,
      formatter: (cell, row) => {
        const quantity = row.quantity?.active ?? 0;
        const costPerUnit = row.costPerUnit;
        if (!costPerUnit || quantity === 0) return 'N/A';
        const totalCost = Number(costPerUnit) * Number(quantity);
        return `$${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
      },
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      headerStyle: () => columnStyles.status,
      style: () => columnStyles.status,
      formatter: (cell) => {
        const statusOption = statusOptions.find(option => option.value === cell);
        const displayLabel = statusOption ? statusOption.label : cell || 'N/A';
        return (
          <Typography
            variant="body2"
            sx={{
              color: cell === 'active' ? 'success.main' : cell === 'inactive' ? 'error.main' : 'warning.main',
              fontWeight: 'medium',
              fontSize: 12,
            }}
          >
            {displayLabel}
          </Typography>
        );
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      headerStyle: () => columnStyles.actions,
      style: () => columnStyles.actions,
      formatter: (cell, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <IconButton
            onClick={() => onEdit(row)}
            title="Edit"
            size="small"
            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' } }}
          >
            <FeatherIcon icon="edit" size={18} />
          </IconButton>
          <IconButton
            onClick={() => onDelete(row)}
            title="Delete"
            size="small"
            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'error.dark' } }}
          >
            <FeatherIcon icon="trash-2" size={18} color="red" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const paginationOptions = {
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  };

  const NoDataIndication = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" width="100%">
      <Box textAlign="center" py={4}>
        <FeatherIcon icon="inbox" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
        <Typography variant="h6" color="text.secondary">
          No Inventory Items
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Add items to your inventory to get started
        </Typography>
      </Box>
    </Box>
  );

  const shouldShowScrollbar = windowWidth <= scrollThreshold;

  const tableStyles = {
    minHeight: '50vh',
    maxHeight: '75vh',
    overflow: shouldShowScrollbar ? 'auto' : 'visible',
    overflowX: shouldShowScrollbar ? 'auto' : 'visible',
    overflowY: shouldShowScrollbar ? 'auto' : 'visible',
    borderRadius: 2,
    boxShadow: 3,
    bgcolor: 'background.paper',
    '& .react-bootstrap-table': {
      minHeight: '50vh',
    },
    '& table': {
      borderCollapse: 'separate',
      borderSpacing: 0,
      tableLayout: 'fixed !important',
      width: '100% !important',
      bgcolor: 'background.paper',
    },
    '& thead th': {
      position: 'sticky',
      top: 0,
      backgroundColor: 'grey.50',
      zIndex: 2,
      borderBottom: '1px solid',
      borderColor: 'grey.200',
      padding: '12px 8px !important',
      fontWeight: '600',
      color: 'text.primary',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& tbody td': {
      padding: '12px 8px !important',
      borderBottom: '1px solid',
      borderColor: 'grey.100',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: 'text.secondary',
    },
    '& thead th:nth-of-type(1), & tbody td:nth-of-type(1)': {
      width: '120px !important',
      minWidth: '120px !important',
      maxWidth: '120px !important',
    },
    '& thead th:nth-of-type(2), & tbody td:nth-of-type(2)': {
      width: '80px !important',
      minWidth: '80px !important',
      maxWidth: '80px !important',
    },
    '& thead th:nth-of-type(3), & tbody td:nth-of-type(3)': {
      width: '100px !important',
      minWidth: '100px !important',
      maxWidth: '100px !important',
    },
    '& thead th:nth-of-type(4), & tbody td:nth-of-type(4)': {
      width: '80px !important',
      minWidth: '80px !important',
      maxWidth: '80px !important',
    },
    '& thead th:nth-of-type(5), & tbody td:nth-of-type(5)': {
      width: '100px !important',
      minWidth: '100px !important',
      maxWidth: '100px !important',
    },
    '& thead th:nth-of-type(6), & tbody td:nth-of-type(6)': {
      width: '100px !important',
      minWidth: '100px !important',
      maxWidth: '100px !important',
    },
    '& thead th:nth-of-type(7), & tbody td:nth-of-type(7)': {
      width: '80px !important',
      minWidth: '80px !important',
      maxWidth: '80px !important',
    },
    '& thead th:nth-of-type(8), & tbody td:nth-of-type(8)': {
      width: '80px !important',
      minWidth: '80px !important',
      maxWidth: '80px !important',
      textAlign: 'center !important',
    },
    '& tbody tr': {
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: 'grey.50',
      },
    },
    '& .pagination': {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: '12px 16px',
      borderTop: '1px solid',
      borderColor: 'grey.200',
      bgcolor: 'grey.50',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
    },
    [`@media (max-width: ${scrollThreshold}px)`]: {
      '& thead th, & tbody td': {
        fontSize: '0.85rem',
        padding: '8px 4px !important',
      },
      '& thead th, & tbody td': {
        width: 'auto !important',
        minWidth: 'auto !important',
        maxWidth: 'none !important',
      },
      '& table': {
        tableLayout: 'auto !important',
      },
    },
    ...(!shouldShowScrollbar && {
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '-ms-overflow-style': 'none',
      'scrollbar-width': 'none',
    }),
  };

  return (
    <Paper sx={tableStyles} className="miui-table scrollable-table">
      <BootstrapTable
        keyField="_id"
        data={items || []}
        columns={columns}
        pagination={paginationFactory(paginationOptions)}
        bootstrap4
        bordered={false}
        hover
        noDataIndication={NoDataIndication}
        wrapperClasses="table-responsive"
        classes='px-2'
      />
    </Paper>
  );
};

export { InventoryItemsTable };